import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, memo } from "react"
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"
import type { Editor } from "@tiptap/core"

// Extensions
import { StarterKit } from "@tiptap/starter-kit"
import { Placeholder } from "@tiptap/extension-placeholder"
import { TextAlign } from "@tiptap/extension-text-align"
import { TaskList } from "@tiptap/extension-task-list"
import { TaskItem } from "@tiptap/extension-task-item"
import { Typography } from "@tiptap/extension-typography"
import { Link } from "@tiptap/extension-link"
import { TextStyle } from "@tiptap/extension-text-style"
import { Color } from "@tiptap/extension-color"
import { Highlight } from "@tiptap/extension-highlight"
import { Underline } from "@tiptap/extension-underline"
import { Markdown } from "tiptap-markdown"
import { marked } from "marked"

// Custom Extensions
import { ImageUploadNode } from "../../tiptap-node/image-upload-node"
import { FileUploadNode } from "../../tiptap-node/file-upload-node"
import { FileNodeExtension } from "../../tiptap-node/file-node"
import { ResizableImageExtension } from "../../tiptap-node/image-node"

// UI Components
import { Toolbar, ToolbarGroup, ToolbarSeparator } from "../../tiptap-ui-primitive/toolbar"
import { Spacer } from "../../tiptap-ui-primitive/spacer"
import { UndoRedoButton } from "../../tiptap-ui/undo-redo-button"
import { MarkButton } from "../../tiptap-ui/mark-button"
import { HeadingDropdownMenu } from "../../tiptap-ui/heading-dropdown-menu"
import { ListDropdownMenu } from "../../tiptap-ui/list-dropdown-menu"
import { BlockquoteButton } from "../../tiptap-ui/blockquote-button"
import { CodeBlockButton } from "../../tiptap-ui/code-block-button"
import { ImageUploadButton } from "../../tiptap-ui/image-upload-button"
import { FileUploadButton } from "../../tiptap-ui/file-upload-button"
import { LinkPopover } from "../../tiptap-ui/link-popover"
import { ColorHighlightPopover } from "../../tiptap-ui/color-highlight-popover"
import { TextAlignButton } from "../../tiptap-ui/text-align-button"
import { ThemeToggle } from "./theme-toggle"

// Utils
import { handleImageUpload, handleFileUpload } from "../../lib/tiptap-utils"

// Styles
import "./simple-editor.scss"

// Функция для проверки, является ли текст markdown (а не HTML)
const isMarkdownText = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false
  
  // Если текст содержит HTML теги, это скорее всего HTML
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(text)
  if (hasHtmlTags) return false
  
  // Проверяем наличие markdown синтаксиса
  const markdownPatterns = [
    /^#{1,6}\s/m, // Заголовки # ## ### и т.д.
    /\*\*[^*]+\*\*/,  // Жирный текст **text**
    /\*[^*]+\*/,      // Курсив *text*
    /__[^_]+__/,      // Жирный текст __text__
    /_[^_]+_/,        // Курсив _text_
    /^\s*[-*+]\s/m,   // Ненумерованные списки
    /^\s*\d+\.\s/m,   // Нумерованные списки
    /^\s*>/m,         // Цитаты
    /`[^`]+`/,        // Inline код
    /```[\s\S]*```/,  // Блоки кода
    /\[.+\]\(.+\)/,   // Ссылки [text](url)
  ]
  
  return markdownPatterns.some(pattern => pattern.test(text))
}

// Функция для конвертации markdown в HTML
const convertMarkdownToHtml = (text: string): string => {
  if (!text) return text
  
  // Если это markdown, преобразуем в HTML
  if (isMarkdownText(text)) {
    return marked(text) as string
  }
  
  return text
}

// Helper function to check if current theme is dark
const isDarkTheme = (): boolean => {
  return document.documentElement.classList.contains('dark') || 
         document.body.classList.contains('dark')
}

// Helper function to determine if a color is light or dark
const isLightColor = (color: string): boolean => {
  // Convert color to RGB
  let r = 0, g = 0, b = 0
  
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16)
      g = parseInt(hex[1] + hex[1], 16)
      b = parseInt(hex[2] + hex[2], 16)
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16)
      g = parseInt(hex.slice(2, 4), 16)
      b = parseInt(hex.slice(4, 6), 16)
    }
  } else if (color.startsWith('rgb')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (match) {
      r = parseInt(match[1])
      g = parseInt(match[2])
      b = parseInt(match[3])
    }
  } else if (color === 'white' || color === '#fff' || color === '#ffffff') {
    return true
  } else if (color === 'black' || color === '#000' || color === '#000000') {
    return false
  }
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

// Check if color is a "problematic" color that should be converted
// (white/light colors in light theme, black/dark colors in dark theme)
const isProblematicColor = (color: string, darkTheme: boolean): boolean => {
  const isLight = isLightColor(color)
  
  // In dark theme, very light colors (like white) are fine
  // In light theme, very light colors (like white) are problematic - can't see them
  // In dark theme, very dark colors (like black) are problematic - can't see them
  // In light theme, very dark colors (like black) are fine
  
  if (darkTheme) {
    // Dark theme: dark colors are problematic
    return !isLight
  } else {
    // Light theme: light colors are problematic
    return isLight
  }
}

// Transform pasted HTML to fix text colors based on theme and preserve line breaks
const transformPastedHtml = (html: string): string => {
  const darkTheme = isDarkTheme()
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  
  // Обрабатываем элементы с колонками (div, table, и т.д.) - преобразуем их в вертикальный текст
  // Но сохраняем форматирование (markdown, списки, и т.д.)
  const columnElements = doc.body.querySelectorAll('div[style*="column"], div[style*="float"], table, .column, [class*="column"]')
  const processedElements = new Set<HTMLElement>()
  
  columnElements.forEach((el) => {
    const element = el as HTMLElement
    if (processedElements.has(element)) return
    
    // Если это таблица, преобразуем каждую ячейку в отдельный параграф, сохраняя форматирование
    if (element.tagName === 'TABLE') {
      const rows = element.querySelectorAll('tr')
      const contentElements: HTMLElement[] = []
      
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td, th')
        cells.forEach((cell) => {
          // Клонируем содержимое ячейки, сохраняя форматирование
          const cellContent = cell.cloneNode(true) as HTMLElement
          // Если ячейка содержит только текст, оборачиваем в параграф
          if (cellContent.children.length === 0 && cellContent.textContent?.trim()) {
            const p = doc.createElement('p')
            p.innerHTML = cellContent.innerHTML || cellContent.textContent || ''
            contentElements.push(p)
          } else if (cellContent.children.length > 0) {
            // Если есть дочерние элементы, сохраняем их структуру
            Array.from(cellContent.children).forEach((child) => {
              contentElements.push(child as HTMLElement)
            })
          }
        })
      })
      
      // Если нашли контент, заменяем таблицу на параграфы с сохранением форматирования
      if (contentElements.length > 0) {
        const newElement = doc.createElement('div')
        contentElements.forEach((contentEl) => {
          newElement.appendChild(contentEl.cloneNode(true))
        })
        element.parentNode?.replaceChild(newElement, element)
        processedElements.add(element)
      }
    }
    // Если это div с колонками или float
    else if (element.style.columnCount || element.style.float || 
             element.classList.contains('column') || 
             element.className.includes('column')) {
      // Клонируем все дочерние элементы, сохраняя их структуру и форматирование
      const children = Array.from(element.children)
      const contentElements: HTMLElement[] = []
      
      if (children.length > 0) {
        // Сохраняем все дочерние элементы с их форматированием
        children.forEach((child) => {
          const cloned = child.cloneNode(true) as HTMLElement
          contentElements.push(cloned)
        })
      } else {
        // Если нет дочерних элементов, берем содержимое напрямую
        const innerHTML = element.innerHTML.trim()
        if (innerHTML) {
          const tempDiv = doc.createElement('div')
          tempDiv.innerHTML = innerHTML
          Array.from(tempDiv.children).forEach((child) => {
            contentElements.push(child as HTMLElement)
          })
        }
      }
      
      // Если нашли контент, заменяем элемент, сохраняя форматирование
      if (contentElements.length > 0) {
        const newElement = doc.createElement('div')
        contentElements.forEach((contentEl) => {
          newElement.appendChild(contentEl)
        })
        element.parentNode?.replaceChild(newElement, element)
        processedElements.add(element)
      }
    }
  })
  
  // Обрабатываем все элементы для сохранения переносов строк
  // Но только для элементов без форматирования, чтобы не сломать markdown
  const allElements = doc.body.querySelectorAll('*')
  allElements.forEach((el) => {
    const element = el as HTMLElement
    
    // Сохраняем переносы строк только в простых текстовых элементах
    // Пропускаем элементы с форматированием (strong, em, code, и т.д.)
    const hasFormatting = element.querySelector('strong, em, b, i, code, u, s, mark, a, span[style]')
    if (!hasFormatting && element.textContent && element.children.length === 0) {
      // Если элемент содержит только текст без форматирования, заменяем \n на <br>
      const text = element.textContent
      if (text.includes('\n')) {
        const lines = text.split('\n')
        element.innerHTML = lines.join('<br>')
      }
    }
    
    const style = element.style
    
    // Check for color in style attribute
    if (style.color) {
      const color = style.color
      
      if (isProblematicColor(color, darkTheme)) {
        // Remove the color - let it inherit from the theme's CSS variable
        element.style.removeProperty('color')
      }
    }
    
    // Also check for color attribute (older HTML)
    const colorAttr = element.getAttribute('color')
    if (colorAttr) {
      if (isProblematicColor(colorAttr, darkTheme)) {
        element.removeAttribute('color')
      }
    }
    
    // Remove background colors that might conflict
    if (style.backgroundColor) {
      const bgColor = style.backgroundColor
      // If background is problematic (opposite of text issue), remove it
      if (!isProblematicColor(bgColor, darkTheme)) {
        // Background is the "opposite" - if it's light in dark theme or dark in light theme
        // it might make text unreadable
        const bgIsLight = isLightColor(bgColor)
        if ((darkTheme && bgIsLight) || (!darkTheme && !bgIsLight)) {
          element.style.removeProperty('background-color')
          element.style.removeProperty('background')
        }
      }
    }
    
    // Удаляем стили, которые могут создавать колонки
    if (style.columnCount || style.columnWidth || style.float === 'left' || style.float === 'right') {
      element.style.removeProperty('column-count')
      element.style.removeProperty('column-width')
      element.style.removeProperty('float')
    }
  })
  
  return doc.body.innerHTML
}

// Types
export type FileUploadFunction = (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal
) => Promise<string>

export type FileUploadErrorHandler = (error: Error) => void

export interface SimpleEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  onFileUpload?: FileUploadFunction
  onFileUploadError?: FileUploadErrorHandler
  onImageUpload?: FileUploadFunction
  onImageUploadError?: FileUploadErrorHandler
  className?: string
  editable?: boolean
}


// Main Editor Toolbar
const MainToolbar = memo(function MainToolbar() {
  return (
    <Toolbar className="simple-editor-toolbar">
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <HeadingDropdownMenu />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="underline" />
        <MarkButton type="strike" />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <ColorHighlightPopover />
        <LinkPopover />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <ListDropdownMenu />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>
      
      <ToolbarSeparator />
      
      <ToolbarGroup>
        <ImageUploadButton />
        <FileUploadButton />
      </ToolbarGroup>

      <Spacer />

      <ThemeToggle />
    </Toolbar>
  )
})

export const SimpleEditor = memo(function SimpleEditor({
  value = "",
  onChange,
  placeholder = "Начните писать...",
  onFileUpload,
  onFileUploadError,
  onImageUpload,
  onImageUploadError,
  className = "",
  editable = true,
}: SimpleEditorProps) {
  // Use refs for stable callbacks
  const imageUploadRef = useRef(onImageUpload)
  const imageUploadErrorRef = useRef(onImageUploadError)
  const fileUploadRef = useRef(onFileUpload)
  const fileUploadErrorRef = useRef(onFileUploadError)
  const onChangeRef = useRef(onChange)

  // Keep refs updated
  useEffect(() => {
    imageUploadRef.current = onImageUpload
    imageUploadErrorRef.current = onImageUploadError
    fileUploadRef.current = onFileUpload
    fileUploadErrorRef.current = onFileUploadError
    onChangeRef.current = onChange
  }, [onImageUpload, onImageUploadError, onFileUpload, onFileUploadError, onChange])

  const handleUpdate = useCallback(({ editor }: { editor: { getHTML: () => string } }) => {
    const html = editor.getHTML()
    onChangeRef.current?.(html)
  }, [])

  const extensions = useMemo(
    () => [
      // Custom extensions first for priority in parsing
      ResizableImageExtension.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: "tiptap-image",
        },
      }),
      FileNodeExtension,
      ImageUploadNode.configure({
        type: "imageResize",
        accept: "image/*",
        maxSize: 5 * 1024 * 1024,
        upload: (file, onProgress, abortSignal) => {
          if (imageUploadRef.current) {
            return imageUploadRef.current(file, onProgress, abortSignal)
          }
          return handleImageUpload(file, onProgress, abortSignal)
        },
        onError: (error) => {
          imageUploadErrorRef.current?.(error)
        },
      }),
      FileUploadNode.configure({
        accept: "*",
        maxSize: 10 * 1024 * 1024,
        upload: (file, onProgress, abortSignal) => {
          if (fileUploadRef.current) {
            return fileUploadRef.current(file, onProgress, abortSignal)
          }
          return handleFileUpload(file, onProgress, abortSignal)
        },
        onError: (error) => {
          fileUploadErrorRef.current?.(error)
        },
      }),
      // Standard extensions
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        codeBlock: {
          HTMLAttributes: {
            class: "tiptap-code-block",
          },
        },
        hardBreak: {
          keepMarks: true, // сохранять форматирование после Shift+Enter
        },
        dropcursor: {
          color: "var(--tt-brand-color-500, #8b5cf6)",
          width: 2,
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Typography,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
      Markdown.configure({
        html: true, // Разрешить HTML в markdown
        tightLists: true, // Компактные списки
        tightListClass: 'tight',
        bulletListMarker: '-',
        linkify: true, // Автоматическое определение ссылок
        breaks: true, // Перенос строк как <br>
        transformPastedText: true, // Преобразовывать вставленный markdown
        transformCopiedText: false, // Не преобразовывать скопированный текст
      }),
    ],
    [placeholder]
  )

  // Используем ref для отслеживания того, было ли внешнее значение уже применено
  const isExternalValueAppliedRef = useRef(false)
  // Отслеживаем последнее значение из редактора (от пользователя)
  const editorValueRef = useRef("")
  
  // Преобразуем markdown в HTML при первоначальной загрузке
  const initialContent = useMemo(() => convertMarkdownToHtml(value), [])
  
  const editor = useEditor({
    extensions,
    content: initialContent,
    editable,
    editorProps: {
      attributes: {
        class: "simple-editor",
      },
      handleKeyDown: (view, event) => {
        const keyboardEvent = event as KeyboardEvent
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editorInstance = (view as unknown as { editor?: Editor }).editor

        if (!editorInstance) {
          return false
        }

        const isBulletList = editorInstance.isActive("bulletList")
        const isOrderedList = editorInstance.isActive("orderedList")
        const isTaskList = editorInstance.isActive("taskList")
        const isAnyList = isBulletList || isOrderedList || isTaskList

        // Enter внутри списка — выйти из списка и продолжить обычным текстом
        if (keyboardEvent.key === "Enter" && keyboardEvent.shiftKey && isAnyList) {
          keyboardEvent.preventDefault()

          const chain = editorInstance.chain().focus()

          if (isTaskList) {
            chain.toggleTaskList().splitBlock().run()
          } else if (isBulletList) {
            chain.toggleBulletList().splitBlock().run()
          } else if (isOrderedList) {
            chain.toggleOrderedList().splitBlock().run()
          }

          return true
        }

        // Backspace на пустом элементе списка — полностью выйти из списка (как Shift+Enter)
        if (keyboardEvent.key === "Backspace") {
          const { state } = editorInstance
          const { $from } = state.selection
          const parent = $from.parent

          const isEmptyParagraph =
            parent.type.name === "paragraph" && parent.content.size === 0

          if (isEmptyParagraph && isAnyList) {
            keyboardEvent.preventDefault()
            const chain = editorInstance.chain().focus()
            if (isTaskList) chain.toggleTaskList().splitBlock().run()
            else if (isBulletList) chain.toggleBulletList().splitBlock().run()
            else if (isOrderedList) chain.toggleOrderedList().splitBlock().run()
            return true
          }
        }

        // Остальные клавиши обрабатываются стандартно Tiptap
        return false
      },
      handlePaste: (view, event, slice) => {
        const clipboardData = event.clipboardData
        if (!clipboardData) return false
        
        // Получаем и plain text и HTML
        const html = clipboardData.getData('text/html')
        const plainText = clipboardData.getData('text/plain')
        
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editorInstance = (view as unknown as { editor?: Editor }).editor
        if (!editorInstance) return false
        
        if (html) {
          // Transform the HTML to fix colors based on theme
          const transformedHtml = transformPastedHtml(html)
          
          // Вставляем HTML с сохранением пробелов и переносов строк
          editorInstance.commands.insertContent(transformedHtml, {
            parseOptions: {
              preserveWhitespace: 'full',
            },
          })
          return true
        } else if (plainText) {
          // Если есть только plain text, обрабатываем его для сохранения переносов строк
          // Разбиваем текст на строки и создаем параграфы для каждой строки
          const lines = plainText.split('\n')
          
          if (lines.length === 1) {
            // Если только одна строка, вставляем как обычный текст
            editorInstance.commands.insertContent(plainText)
            return true
          }
          
          // Если несколько строк, создаем параграфы для каждой
          const paragraphs = lines.map((line) => {
            const trimmed = line.trim()
            // Если строка пустая, создаем пустой параграф
            if (trimmed === '') {
              return '<p><br></p>'
            }
            // Иначе создаем параграф с текстом
            return `<p>${line}</p>`
          })
          
          editorInstance.commands.insertContent(paragraphs.join(''), {
            parseOptions: {
              preserveWhitespace: 'full',
            },
          })
          return true
        }
        
        return false
      },
    },
    onUpdate: handleUpdate,
    immediatelyRender: false,
  })

  // Sync external value changes
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    
    const currentEditorHtml = editor.getHTML()
    
    // Преобразуем markdown в HTML если нужно
    const convertedValue = convertMarkdownToHtml(value)
    
    // Проверяем, изменилось ли value извне (не совпадает с текущим содержимым редактора)
    // И это не тот же HTML который сейчас в редакторе
    const isExternalChange = convertedValue !== currentEditorHtml && convertedValue !== editorValueRef.current
    
    if (isExternalChange) {
      isExternalValueAppliedRef.current = true
      
      // Используем queueMicrotask для более точной синхронизации
      queueMicrotask(() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(convertedValue || "", {
            emitUpdate: false,
            parseOptions: {
              preserveWhitespace: "full",
            },
          })
          editorValueRef.current = convertedValue
        }
      })
    }
  }, [editor, value])
  
  // Обновляем ref при изменениях из редактора (от пользователя)
  useEffect(() => {
    if (editor) {
      const updateHandler = () => {
        editorValueRef.current = editor.getHTML()
      }
      editor.on("update", updateHandler)
      return () => {
        editor.off("update", updateHandler)
      }
    }
  }, [editor])

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  if (!editor) {
    return null
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <div className={`simple-editor-wrapper ${className}`}>
        <MainToolbar />
        <EditorContent editor={editor} className="simple-editor-content" />
      </div>
    </EditorContext.Provider>
  )
})

export default SimpleEditor
